import { parseNumberInput, parseNumberList } from "@/lib/parse-number";

/**
 * Species abundance lists, the input to the diversity indices.
 *
 * Two shapes are accepted:
 * - one species per line, a name then a count (`Oak 25`, `Oak, 25`, `Oak: 25`, a tab);
 *   a line that is only a number is an unnamed species;
 * - or nothing but numbers (`25 18 12`, `25, 18, 12`), each number one species.
 *
 * Zero counts are dropped (an absent species adds nothing to any index) but counted,
 * so the widget can say so.
 */

export interface Abundance {
  label: string;
  count: number;
}

export interface ParsedAbundances {
  entries: Abundance[];
  /** Species listed with a count of zero. */
  zeros: number;
  /** Lines that held no readable count. */
  unreadable: string[];
  /** Counts below zero were found. */
  negative: boolean;
}

const NUMBERS_ONLY = /^[\d\s.,;]+$/;
const NAME_THEN_COUNT = /^(.*?)[\s,:;=]+(-?\d[\d.,]*)\s*$/;
const COUNT_ONLY = /^-?\d[\d.,]*$/;
const LINE_BREAKS = /\r?\n/;
const BULLET = /^[-*•]\s+/;

function entryFromLine(line: string): { label: string; value: number } | null {
  const plain = COUNT_ONLY.test(line);
  const match = plain ? null : NAME_THEN_COUNT.exec(line);
  if (!(plain || match)) {
    return null;
  }
  const value = parseNumberInput(plain ? line : (match?.[2] ?? ""));
  return value === null
    ? null
    : { label: plain ? "" : (match?.[1] ?? "").trim(), value };
}

interface Row {
  label: string;
  value: number;
}

/** The rows of a list: numbers only, or one `name count` per line. */
function readRows(text: string, unreadable: string[]): Row[] {
  if (NUMBERS_ONLY.test(text)) {
    return parseNumberList(text).map((value) => ({ label: "", value }));
  }
  const rows: Row[] = [];
  for (const raw of text.split(LINE_BREAKS)) {
    const line = raw.trim().replace(BULLET, "");
    if (line === "") {
      continue;
    }
    const row = entryFromLine(line);
    if (row) {
      rows.push(row);
    } else {
      unreadable.push(line);
    }
  }
  return rows;
}

export function parseAbundances(
  text: string,
  unnamed: (position: number) => string
): ParsedAbundances {
  const result: ParsedAbundances = {
    entries: [],
    zeros: 0,
    unreadable: [],
    negative: false,
  };
  const trimmed = text.trim();
  if (trimmed === "") {
    return result;
  }
  const rows = readRows(trimmed, result.unreadable);
  for (const [position, row] of rows.entries()) {
    if (row.value < 0) {
      result.negative = true;
    } else if (row.value === 0) {
      result.zeros += 1;
    } else {
      result.entries.push({
        label: row.label || unnamed(position + 1),
        count: row.value,
      });
    }
  }
  return result;
}

export function totalOf(entries: readonly Abundance[]): number {
  let total = 0;
  for (const entry of entries) {
    total += entry.count;
  }
  return total;
}

export function allWholeNumbers(entries: readonly Abundance[]): boolean {
  return entries.every((entry) => Number.isInteger(entry.count));
}
